import React, { useCallback, useEffect, useState } from 'react';
import { fastapiService } from '../../services/fastapiService';
import { getEnvVar, getEnvVarOptional } from '../../config/env';

/**
 * Copilot / AI action metrics: Prometheus exposition + Grafana dashboard import.
 * Backend exposes ``sws_copilot_actions_total`` and ``sws_copilot_action_duration_seconds``.
 */
export const CopilotMetricsPage: React.FC = () => {
  const [text, setText] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const grafanaUrl = getEnvVarOptional('VITE_GRAFANA_COPILOT_DASHBOARD_URL').trim();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await fastapiService.getAdminPrometheusText();
      setText(body || '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load metrics');
      setText('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const baseUrl = (() => {
    try {
      return getEnvVar('VITE_FASTAPI_BASE_URL').replace(/\/$/, '');
    } catch {
      return '';
    }
  })();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Copilot / AI action metrics</h1>
        <p className="mt-1 text-sm text-admin-600">
          Prometheus counters and histograms for copilot action success rate, latency, and failure reasons (
          <code className="text-xs bg-admin-100 px-1 rounded">error_code</code> label).
        </p>
      </div>

      <div className="admin-card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-admin-900">Scrape targets</h2>
        <ul className="list-disc pl-5 text-sm text-admin-700 space-y-1">
          <li>
            <strong>Public scrape</strong> (when <code className="text-xs bg-admin-100 px-1">PROMETHEUS_METRICS_ENABLED</code> is
            true):{' '}
            <code className="text-xs break-all bg-admin-100 px-1 rounded">{baseUrl}/metrics</code>
          </li>
          <li>
            <strong>Authenticated</strong> (same series, admin JWT):{' '}
            <code className="text-xs break-all bg-admin-100 px-1 rounded">
              {baseUrl}/api/v1/admin/monitoring/prometheus
            </code>
          </li>
        </ul>
        <p className="text-sm text-admin-600">
          Import the bundled Grafana dashboard JSON from{' '}
          <code className="text-xs bg-admin-100 px-1">admin-app/monitoring/grafana/copilot-actions-dashboard.json</code> (Grafana →
          Dashboards → Import). Point the Prometheus datasource at the job that scrapes{' '}
          <code className="text-xs bg-admin-100 px-1">/metrics</code>.
        </p>
      </div>

      {grafanaUrl ? (
        <div className="admin-card p-4">
          <h2 className="text-lg font-semibold text-admin-900 mb-2">Grafana</h2>
          <div className="aspect-video w-full min-h-[360px] rounded border border-admin-200 overflow-hidden bg-admin-50">
            <iframe title="Grafana Copilot dashboard" src={grafanaUrl} className="h-full w-full border-0" />
          </div>
        </div>
      ) : null}

      <div className="admin-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-admin-900">Prometheus exposition (preview)</h2>
          <button type="button" onClick={() => void load()} className="admin-button h-9 px-4 bg-primary-600 text-white hover:bg-primary-700" disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {error ? <p className="text-sm text-red-600 mb-2">{error}</p> : null}
        <textarea
          readOnly
          className="w-full h-96 font-mono text-xs bg-admin-950 text-admin-100 p-3 rounded border border-admin-700"
          value={loading ? 'Loading…' : text}
          spellCheck={false}
        />
      </div>
    </div>
  );
};
